import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Banknote, Coins, Lightbulb, Wallet } from 'lucide-react';
import { isFiniteNumber, normalizeCents as normalizeCentsOrNull, formatMoneyDisplay } from '../../utils/moneyFormatters';
import { getMoneyStyles, getDenominationDisplay, isBill, EURO_BILLS_CENTS, EURO_COINS_CENTS } from '../../practice/components/moneyStyles';
// ── Types ────────────────────────────────────────────────────
export interface MoneyBreakdown {
  bills: {
    value: number;
    count: number;
  }[];
  coins: {
    value: number;
    count: number;
  }[];
}
export interface MoneyVisualProps {
  /** Amount in cents */
  amount: number;
  /** Explicit breakdown (overrides auto-breakdown) */
  breakdown?: MoneyBreakdown;
  showCoins?: boolean;
  showBills?: boolean;
  interactive?: boolean;
  label?: string;
  /** 'stimulus' = compact context visual (no answer leaking), 'support' = full educational panel */
  mode?: 'stimulus' | 'support';
}
// ── Helpers ──────────────────────────────────────────────────
function normalizeCents(amount: number): number {
  return normalizeCentsOrNull(amount) ?? 0;
}
function normalizeBreakdown(breakdown?: MoneyBreakdown): MoneyBreakdown | null {
  if (!breakdown) return null;
  const bills = Array.isArray(breakdown.bills) ? breakdown.bills : [];
  const coins = Array.isArray(breakdown.coins) ? breakdown.coins : [];
  const safeBills = bills.filter(b => isFiniteNumber(b?.value) && isFiniteNumber(b?.count)).map(b => ({
    value: Math.round(b.value),
    count: Math.max(0, Math.round(b.count))
  })).filter(b => b.count > 0);
  const safeCoins = coins.filter(c => isFiniteNumber(c?.value) && isFiniteNumber(c?.count)).map(c => ({
    value: Math.round(c.value),
    count: Math.max(0, Math.round(c.count))
  })).filter(c => c.count > 0);
  return {
    bills: safeBills,
    coins: safeCoins
  };
}
function autoBreakdown(cents: number): MoneyBreakdown {
  let remaining = cents;
  const bills: {
    value: number;
    count: number;
  }[] = [];
  const coins: {
    value: number;
    count: number;
  }[] = [];
  for (const billCents of EURO_BILLS_CENTS) {
    const billEuro = billCents / 100;
    const count = Math.floor(remaining / billCents);
    if (count > 0) {
      bills.push({
        value: billEuro,
        count
      });
      remaining -= count * billCents;
    }
  }
  for (const coinValue of EURO_COINS_CENTS) {
    const count = Math.floor(remaining / coinValue);
    if (count > 0) {
      coins.push({
        value: coinValue,
        count
      });
      remaining -= count * coinValue;
    }
  }
  return {
    bills,
    coins
  };
}
function formatMoney(cents: number): string {
  return formatMoneyDisplay(normalizeCents(cents));
}
// ── Token Renderer ───────────────────────────────────────────
function MoneyToken({
  cents,
  index,
  compact = false
}: {
  cents: number;
  index: number;
  compact?: boolean;
}) {
  const styles = getMoneyStyles(cents);
  const label = getDenominationDisplay(cents);
  const scaleClass = compact ? 'scale-75 origin-center' : '';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: index * 0.04,
    duration: 0.25
  }} className={`${styles.container} ${styles.bg} ${styles.border} flex items-center justify-center relative overflow-hidden ${scaleClass}`} aria-label={label} data-id="element-9600">
      {styles.inner && <div className={`absolute inset-1 rounded ${styles.inner}`} data-id="element-9601" />}
      <div className="relative z-10 flex items-center justify-center" data-id="element-9602">
        <span className={styles.text} data-id="element-9603">{label}</span>
      </div>
    </motion.div>;
}
// ── Stimulus Mode ────────────────────────────────────────────
// Shows coins/bills as visual context WITHOUT revealing the total or breakdown.
// ONLY uses explicit breakdown. Never auto-generates.
function StimulusMode({
  breakdown,
  showBills,
  showCoins,
  label
}: {
  breakdown: MoneyBreakdown | null;
  showBills: boolean;
  showCoins: boolean;
  label?: string;
}) {
  const billTokens = showBills && breakdown ? breakdown.bills.flatMap(bill => Array.from({
    length: Math.min(bill.count, 5)
  }, (_, i) => ({
    cents: bill.value * 100,
    key: `bill-${bill.value}-${i}`
  }))) : [];
  const coinTokens = showCoins && breakdown ? breakdown.coins.flatMap(coin => Array.from({
    length: Math.min(coin.count, 10)
  }, (_, i) => ({
    cents: coin.value,
    key: `coin-${coin.value}-${i}`
  }))) : [];
  const allTokens = [...billTokens, ...coinTokens];
  // If no explicit breakdown is provided, show a minimal label (if any)
  if (allTokens.length === 0) {
    if (!label) return null;
    return <div className="w-full max-w-sm mx-auto" data-id="element-9604">
        <div className="bg-white rounded-xl p-3 border border-slate-200 flex items-center justify-center gap-2 shadow-sm" data-id="element-9605">
          <Wallet className="w-4 h-4 text-slate-400" data-id="element-9606" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-9607">{label}</span>
        </div>
      </div>;
  }
  return <div className="w-full max-w-md mx-auto" data-id="element-9608">
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-id="element-9609">
        {label && <div className="mb-3 text-center" data-id="element-9610">
            <span className="text-sm font-semibold text-slate-600" data-id="element-9611">
              {label}
            </span>
          </div>}
        <div className="flex flex-wrap gap-2 justify-center" data-id="element-9612">
          {allTokens.map((token, i) => <MoneyToken key={token.key} cents={token.cents} index={i} compact data-id="element-9613" />)}
        </div>
      </div>
    </div>;
}
// ── Support Mode ─────────────────────────────────────────────
// Full educational panel with breakdown, calculation, and tip.
// Shown after a wrong answer as scaffolding.
function SupportMode({
  safeAmount,
  actualBreakdown,
  showBills,
  showCoins,
  label
}: {
  safeAmount: number;
  actualBreakdown: MoneyBreakdown;
  showBills: boolean;
  showCoins: boolean;
  label?: string;
}) {
  return <div className="w-full max-w-lg mx-auto" data-id="element-9614">
      {/* Amount header */}
      <div className="bg-white rounded-xl p-5 mb-4 border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-9615">
        <span className="text-sm font-semibold text-slate-600" data-id="element-9616">
          {label || 'Totaal bedrag:'}
        </span>
        <span className="text-2xl font-black text-slate-800" data-id="element-9617">
          {formatMoney(safeAmount)}
        </span>
      </div>

      {/* Breakdown sections */}
      <div className="space-y-4" data-id="element-9618">
        {showBills && actualBreakdown.bills.length > 0 && <BreakdownSection title="Biljetten" icon={<Banknote className="w-5 h-5 text-slate-500" data-id="element-9620" />} items={actualBreakdown.bills.map(bill => ({
        cents: bill.value * 100,
        count: bill.count,
        displayValue: `€${bill.value}`,
        totalDisplay: `€${bill.count * bill.value}`
      }))} startDelay={0} data-id="element-9619" />}

        {showCoins && actualBreakdown.coins.length > 0 && <BreakdownSection title="Munten" icon={<Coins className="w-5 h-5 text-slate-500" data-id="element-9622" />} items={actualBreakdown.coins.map(coin => ({
        cents: coin.value,
        count: coin.count,
        displayValue: getDenominationDisplay(coin.value),
        totalDisplay: formatMoney(coin.count * coin.value)
      }))} startDelay={actualBreakdown.bills.length} data-id="element-9621" />}
      </div>

      {/* Educational tip */}
      <div className="mt-4 bg-slate-50 rounded-xl p-4 border border-slate-200 flex items-start gap-3" data-id="element-9623">
        <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-9624" />
        <div data-id="element-9625">
          <h4 className="text-sm font-bold text-slate-800 mb-1" data-id="element-9626">Tip</h4>
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-9627">
            Kies eerst grote biljetten, daarna kleinere, en werk af met munten.
            Zo heb je meestal minder stuks nodig.
          </p>
        </div>
      </div>
    </div>;
}
// ── Breakdown Section ────────────────────────────────────────
interface BreakdownItem {
  cents: number;
  count: number;
  displayValue: string;
  totalDisplay: string;
}
function BreakdownSection({
  title,
  icon,
  items,
  startDelay
}: {
  title: string;
  icon: React.ReactNode;
  items: BreakdownItem[];
  startDelay: number;
}) {
  return <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-id="element-9628">
      <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2" data-id="element-9629">
        {icon}
        <span data-id="element-9630">{title}</span>
      </h4>

      <div className="space-y-4" data-id="element-9631">
        {items.map((item, idx) => {
        const showCount = Math.min(item.count, isBill(item.cents) ? 5 : 10);
        return <motion.div key={`${title}-${item.cents}`} initial={{
          opacity: 0,
          x: -15
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: (startDelay + idx) * 0.08,
          duration: 0.3
        }} className="flex items-center gap-4" data-id="element-9632">
              <div className="flex gap-1.5 flex-wrap" data-id="element-9633">
                {Array.from({
              length: showCount
            }).map((_, i) => <MoneyToken key={i} cents={item.cents} index={i} compact data-id="element-9634" />)}
                {item.count > showCount && <div className="flex items-center justify-center px-2 py-1 text-slate-600 font-bold text-xs bg-slate-100 rounded-lg border border-slate-200" data-id="element-9635">
                    +{item.count - showCount}
                  </div>}
              </div>

              <div className="flex-1 text-right" data-id="element-9636">
                <span className="text-sm text-slate-500 font-medium" data-id="element-9637">
                  {item.count} × {item.displayValue} ={' '}
                  <strong className="text-slate-800 font-bold text-base" data-id="element-9638">
                    {item.totalDisplay}
                  </strong>
                </span>
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
}
// ── Main Component ───────────────────────────────────────────
export function MoneyVisual({
  amount,
  breakdown,
  showCoins = true,
  showBills = true,
  interactive = false,
  // Kept for prop compatibility, but not used visually here
  label,
  mode = 'support'
}: MoneyVisualProps) {
  const safeAmount = useMemo(() => normalizeCents(amount), [amount]);
  const explicitBreakdown = useMemo(() => normalizeBreakdown(breakdown), [breakdown]);
  // Support mode uses explicit breakdown if provided, otherwise auto-generates
  const supportBreakdown = useMemo(() => explicitBreakdown ?? autoBreakdown(safeAmount), [explicitBreakdown, safeAmount]);
  if (mode === 'stimulus') {
    return <StimulusMode breakdown={explicitBreakdown} showBills={showBills} showCoins={showCoins} label={label} data-id="element-9639" />;
  }
  return <SupportMode safeAmount={safeAmount} actualBreakdown={supportBreakdown} showBills={showBills} showCoins={showCoins} label={label} data-id="element-9640" />;
}